import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Row, Col, Spinner, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from "react-toastify";
import { getClaimedLoanTasks, getUnclaimedLoanTasks } from "../../../services/loanService";
import { BpmTaskWithApplication, BpmTaskWorkItemType } from "../../../services/schemas/loanServiceSchemas";
import { formatCurrency } from "../../../utils/formatters";
import './WorkItemView.css';

interface WorkItemViewProps {}

const WorkItemView: React.FC<WorkItemViewProps> = () => {
    const { taskId } = useParams<{ taskId: string }>();
    const navigate = useNavigate();
    
    const [taskWithApp, setTaskWithApp] = useState<BpmTaskWithApplication | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Get applicable process steps based on application details
    const getApplicableProcessSteps = (): BpmTaskWorkItemType[] => {
        const steps: BpmTaskWorkItemType[] = [];
        
        if (taskWithApp?.application?.loanLeadId) {
            steps.push(BpmTaskWorkItemType.PROCESS_LEAD);
        }
        
        steps.push(
            BpmTaskWorkItemType.CAPTURE_APPLICATION,
            BpmTaskWorkItemType.UPLOAD_DOCUMENTS,
            BpmTaskWorkItemType.VERIFY_DOCUMENTS
        );

        // Collateral comes before credit assessment, for a secured loan with collateral captured.
        if (taskWithApp?.application?.collaterals && taskWithApp.application.collaterals.length > 0
            && taskWithApp.application.loanDetails?.securityType !== 'UNSECURED') {
            steps.push(BpmTaskWorkItemType.VERIFY_COLLATERAL);
        }

        steps.push(
            BpmTaskWorkItemType.CREDIT_ASSESSMENT,
            BpmTaskWorkItemType.APPROVE_LOAN,
            BpmTaskWorkItemType.UPLOAD_AGREEMENT,
            BpmTaskWorkItemType.DISBURSE_FUNDS
        );
        
        return steps;
    };

    const processSteps = useMemo(() => (taskWithApp ? getApplicableProcessSteps() : []), [taskWithApp]);

    // Fetch task details
    const fetchTaskDetails = async () => {
        if (!taskId) {
            setError(new Error('Task ID is required'));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Fetch both claimed and unclaimed tasks to find the specific task
            const [claimedTasksResponse, unclaimedTasksResponse] = await Promise.all([
                getClaimedLoanTasks({ page: 0, size: 1000 }),
                getUnclaimedLoanTasks({ page: 0, size: 1000 })
            ]);

            const claimedTasks = claimedTasksResponse.content || [];
            const unclaimedTasks = unclaimedTasksResponse.content || [];
            const allTasks = [...claimedTasks, ...unclaimedTasks];
            
            const foundTask = allTasks.find(t => t.task.taskId === taskId);

            if (foundTask) {
                setTaskWithApp(foundTask);
            } else {
                setError(new Error('Task not found'));
            }
        } catch (error) {
            console.error('Error fetching task details:', error);
            setError(error instanceof Error ? error : new Error('Failed to fetch task details'));
            toast.error('Failed to fetch task details');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTaskDetails();
    }, [taskId]);

    // Format work item type for display
    const formatWorkItemType = (type: BpmTaskWorkItemType) => {
        return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    };

    // Get custom badge style for work item types
    const getWorkItemTypeBadgeStyle = (type: BpmTaskWorkItemType): React.CSSProperties => {
        switch (type) {
            case BpmTaskWorkItemType.PROCESS_LEAD:
                return { backgroundColor: '#212529', color: 'white' };
            case BpmTaskWorkItemType.CAPTURE_APPLICATION:
                return { backgroundColor: '#0d6efd', color: 'white' };
            case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
                return { backgroundColor: '#0dcaf0', color: 'black' };
            case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
                return { backgroundColor: '#ffc107', color: 'black' };
            case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
                return { backgroundColor: '#dc3545', color: 'white' };
            case BpmTaskWorkItemType.VERIFY_COLLATERAL:
                return { backgroundColor: '#6f42c1', color: 'white' };
            case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
                return { backgroundColor: '#fd7e14', color: 'white' };
            case BpmTaskWorkItemType.DISBURSE_FUNDS:
                return { backgroundColor: '#28a745', color: 'white' };
            default:
                return { backgroundColor: '#6c757d', color: 'white' };
        }
    };

    // Get icon for work item types
    const getWorkItemTypeIcon = (type: BpmTaskWorkItemType) => {
        switch (type) {
            case BpmTaskWorkItemType.PROCESS_LEAD:
                return 'bi-person-plus';
            case BpmTaskWorkItemType.CAPTURE_APPLICATION:
                return 'bi-file-earmark-text';
            case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
                return 'bi-cloud-upload';
            case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
                return 'bi-file-check';
            case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
                return 'bi-cash-stack';
            case BpmTaskWorkItemType.VERIFY_COLLATERAL:
                return 'bi-shield-check';
            case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
                return 'bi-file-earmark-contract';
            case BpmTaskWorkItemType.DISBURSE_FUNDS:
                return 'bi-cash';
            default:
                return 'bi-tasks';
        }
    };

    // Get current step index
    const getCurrentStepIndex = () => {
        if (!taskWithApp) return -1;
        return processSteps.indexOf(taskWithApp.task.workItemType);
    };

    // Get next steps
    const getNextSteps = () => {
        const currentIndex = getCurrentStepIndex();
        if (currentIndex === -1 || currentIndex === processSteps.length - 1) return [];
        return processSteps.slice(currentIndex + 1);
    };

    // Get time ago string
    const getTimeAgo = (date: string) => {
        const now = new Date();
        const taskDate = new Date(date);
        const diffInMs = now.getTime() - taskDate.getTime();
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInDays > 0) {
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        } else if (diffInHours > 0) {
            return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        } else {
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
        }
    };

    // Get due date status
    const getDueDateStatus = (dueDate: string) => {
        const now = new Date();
        const due = new Date(dueDate);
        const diffInMs = due.getTime() - now.getTime();
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMs < 0) {
            return { status: 'overdue', text: 'Overdue', variant: 'danger' };
        } else if (diffInDays === 0) {
            return { status: 'due-today', text: 'Due Today', variant: 'warning' };
        } else if (diffInDays <= 1) {
            return { status: 'due-soon', text: 'Due Tomorrow', variant: 'warning' };
        } else {
            return { status: 'on-time', text: `Due in ${diffInDays} days`, variant: 'success' };
        }
    };

    const getCustomerName = () => {
        if (!taskWithApp) return 'Unknown Customer';
        return taskWithApp.task.company?.companyName ||
               taskWithApp.application?.company?.companyName ||
               taskWithApp.application?.individual?.fullNames ||
               taskWithApp.task.fullName ||
               'Unknown Customer';
    };

    const getPrimaryContact = () => {
        if (!taskWithApp) return { phone: null, email: null };

        const application = taskWithApp.application;
        const taskCompany = taskWithApp.task.company;

        const individualPhone = application?.individual?.cellNumber || application?.individual?.telNumber;
        const individualEmail = application?.individual?.email;

        const applicationCompanyPhone = application?.company?.contactTel;
        const applicationCompanyEmail = application?.company?.contactEmail;

        const taskCompanyPhone = taskCompany?.contactTel;
        const taskCompanyEmail = taskCompany?.contactEmail;

        return {
            phone: individualPhone || applicationCompanyPhone || taskCompanyPhone || null,
            email: individualEmail || applicationCompanyEmail || taskCompanyEmail || null
        };
    };

    const contactInfo = getPrimaryContact();

    if (isLoading) {
        return (
            <div className="work-item-view">
                <Container fluid className="py-4">
                    <div className="loading-container">
                        <div className="loading-content">
                            <div className="loading-spinner">
                                <Spinner animation="border" variant="primary" size="sm" />
                            </div>
                            <h5 className="loading-text">Loading Work Item</h5>
                            <p className="loading-subtitle">Please wait while we fetch the details...</p>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (error || !taskWithApp) {
        return (
            <div className="work-item-view">
                <Container fluid className="py-4">
                    {/* Breadcrumb */}
                    <nav className="breadcrumb-nav mb-4">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/" className="breadcrumb-link">
                                    <i className="bi bi-house-door"></i>
                                    Home
                                </Link>
                            </li>
                            <li className="breadcrumb-item">
                                <Link to="/work-item-tracker" className="breadcrumb-link">
                                    Work Item Tracker
                                </Link>
                            </li>
                            <li className="breadcrumb-item active">Work Item Details</li>
                        </ol>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigate('/work-item-tracker')}
                            className="back-button"
                        >
                            <i className="bi bi-arrow-left"></i>
                            Back to Tracker
                        </Button>
                    </nav>

                    <div className="error-container">
                        <div className="error-content">
                            <div className="error-icon">
                                <i className="bi bi-exclamation-triangle"></i>
                            </div>
                            <h3>Work Item Not Found</h3>
                            <p>{error?.message || 'The requested work item could not be found.'}</p>
                            <Button
                                variant="primary"
                                onClick={() => navigate('/work-item-tracker')}
                                className="mt-3"
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Back to Work Item Tracker
                            </Button>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    const task = taskWithApp.task;
    const application = taskWithApp.application;
    const dueDateStatus = task.dueDate ? getDueDateStatus(task.dueDate) : null;
    const currentStepIndex = getCurrentStepIndex();
    const currentStepLabel = currentStepIndex >= 0 ? formatWorkItemType(processSteps[currentStepIndex]) : 'Not Started';
    const nextSteps = getNextSteps();

    const overviewStats = [
        {
            key: 'loan-amount',
            label: 'Loan Amount',
            value: application?.loanDetails?.loanAmount ? formatCurrency(application.loanDetails.loanAmount) : 'N/A',
            icon: 'bi-cash-stack',
            accent: 'text-success'
        },
        {
            key: 'current-step',
            label: 'Current Step',
            value: currentStepLabel,
            icon: 'bi-diagram-3',
            accent: 'text-primary',
            tooltip: 'Based on the BPM work item type'
        },
        {
            key: 'due-date',
            label: 'Due Date',
            value: task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date',
            icon: 'bi-calendar-event',
            accent: 'text-warning',
            badge: dueDateStatus?.text,
            badgeVariant: dueDateStatus?.variant || 'secondary'
        },
        {
            key: 'task-age',
            label: 'Task Age',
            value: getTimeAgo(task.createdDate),
            icon: 'bi-stopwatch',
            accent: 'text-info',
            tooltip: `Created on ${new Date(task.createdDate).toLocaleString()}`
        }
    ];

    return (
        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
            <Container fluid>
                {/* Breadcrumb */}
                <div className="row mb-3">
                    <div className="col">
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/work-item-tracker">Application Tracker</Link></li>
                                    <li className="breadcrumb-item active">Work Item Details</li>
                                </ol>
                            </div>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate('/work-item-tracker')}
                            >
                                <i className="bi bi-arrow-left me-1"></i>
                                Back to Tracker
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Header Card */}
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Body className="p-4">
                        <Row className="align-items-center">
                            <Col md={8}>
                                <div className="d-flex align-items-start">
                                    <div className="me-3">
                                        <div className="rounded-circle p-3" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                                            <i className={`bi ${getWorkItemTypeIcon(task.workItemType)} fs-2 text-primary`}></i>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2">{formatWorkItemType(task.workItemType)}</h3>
                                        <p className="text-muted mb-2">
                                            <i className="bi bi-building me-2"></i>
                                            <strong>{getCustomerName()}</strong>
                                        </p>
                                        {application?.applicationNumber && (
                                            <p className="text-muted mb-0">
                                                <i className="bi bi-file-text me-2"></i>
                                                Application: <strong>{application.applicationNumber}</strong>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </Col>
                            <Col md={4} className="text-md-end">
                                <div className="d-flex flex-column align-items-md-end gap-2">
                                    <span
                                        className="badge"
                                        style={getWorkItemTypeBadgeStyle(task.workItemType)}
                                    >
                                        <i className={`bi ${getWorkItemTypeIcon(task.workItemType)} me-1`}></i>
                                        {formatWorkItemType(task.workItemType)}
                                    </span>
                                    {task.assignee ? (
                                        <span className="badge" style={{ backgroundColor: '#198754', color: 'white' }}>
                                            <i className="bi bi-person-check-fill me-1"></i>
                                            Assigned to {task.assignee}
                                        </span>
                                    ) : (
                                        <span className="badge" style={{ backgroundColor: '#ffc107', color: 'black' }}>
                                            <i className="bi bi-person-x-fill me-1"></i>
                                            Unassigned
                                        </span>
                                    )}
                                    {dueDateStatus && (
                                        <Badge bg={dueDateStatus.variant}>
                                            <i className="bi bi-clock me-1"></i>
                                            {dueDateStatus.text}
                                        </Badge>
                                    )}
                                </div>
                            </Col>
                        </Row>
                        <div className="d-flex flex-wrap justify-content-between align-items-center mt-4">
                            <div className="text-muted small">
                                Task ID: <span className="font-monospace">{task.taskId}</span>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                {application?.id && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => navigate(`/application/${application.id}`)}
                                    >
                                        <i className="bi bi-eye me-1"></i>
                                        View Application
                                    </Button>
                                )}
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => navigate('/work-item-tracker')}
                                >
                                    <i className="bi bi-arrow-left me-1"></i>
                                    Back to Tracker
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Overview Stats */}
                <Row className="g-3 mb-4">
                    {overviewStats.map((stat) => (
                        <Col key={stat.key} xs={12} md={6} xl={3}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Body className="d-flex align-items-center">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                        style={{
                                            width: '52px',
                                            height: '52px',
                                            backgroundColor: 'rgba(13, 110, 253, 0.08)'
                                        }}
                                    >
                                        <i className={`bi ${stat.icon} fs-4 text-primary`}></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="text-muted small mb-1 d-flex align-items-center">
                                            {stat.tooltip ? (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip id={`tooltip-${stat.key}`}>{stat.tooltip}</Tooltip>}
                                                >
                                                    <span className="d-inline-flex align-items-center cursor-pointer">
                                                        {stat.label}
                                                        <i className="bi bi-info-circle ms-1"></i>
                                                    </span>
                                                </OverlayTrigger>
                                            ) : (
                                                stat.label
                                            )}
                                        </div>
                                        <div className={`fw-bold fs-5 ${stat.accent || ''}`}>
                                            {stat.value}
                                        </div>
                                        {stat.badge && (
                                            <Badge bg={stat.badgeVariant} className="mt-2">
                                                {stat.badge}
                                            </Badge>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Process Flow Timeline */}
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom">
                        <div className="d-flex align-items-center justify-content-between">
                            <h5 className="mb-0">
                                <i className="bi bi-diagram-3 me-2"></i>
                                Loan Application Process Flow
                            </h5>
                            <small className="text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                Showing {processSteps.length} applicable steps
                            </small>
                        </div>
                    </Card.Header>
                    <Card.Body className="p-4">
                        {/* Conditional steps info */}
                        {(!application?.loanLeadId || !application?.collaterals?.length) && (
                            <div className="alert alert-info mb-4" style={{ fontSize: '0.875rem' }}>
                                <i className="bi bi-info-circle-fill me-2"></i>
                                <strong>Note:</strong>
                                {!application?.loanLeadId && ' "Process Lead" step is not applicable (no lead data).'}
                                {!application?.loanLeadId && !application?.collaterals?.length && ' '}
                                {!application?.collaterals?.length && ' "Verify Collateral" step is not applicable (no collaterals).'}
                            </div>
                        )}
                        
                        <div className="process-flow">
                            {processSteps.map((step, index) => {
                                const isCompleted = index < currentStepIndex;
                                const isCurrent = index === currentStepIndex;
                                const isPending = index > currentStepIndex;
                                const isOptional = step === BpmTaskWorkItemType.PROCESS_LEAD || step === BpmTaskWorkItemType.VERIFY_COLLATERAL;
                                
                                return (
                                    <div key={step} className="d-flex align-items-center mb-3">
                                        <div className="d-flex align-items-center" style={{ minWidth: '280px' }}>
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    backgroundColor: isCompleted ? '#28a745' : isCurrent ? '#0d6efd' : '#e9ecef',
                                                    color: isCompleted || isCurrent ? 'white' : '#6c757d'
                                                }}
                                            >
                                                {isCompleted ? (
                                                    <i className="bi bi-check-lg"></i>
                                                ) : (
                                                    <i className={`bi ${getWorkItemTypeIcon(step)}`}></i>
                                                )}
                                            </div>
                                            <div className="ms-3 flex-grow-1">
                                                <div className={`fw-${isCurrent ? 'bold' : 'medium'}`}>
                                                    {formatWorkItemType(step)}
                                                    {isOptional && (
                                                        <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.65rem' }}>
                                                            Conditional
                                                        </Badge>
                                                    )}
                                                </div>
                                                {isCurrent && (
                                                    <small className="text-primary">
                                                        <i className="bi bi-arrow-right-circle-fill me-1"></i>
                                                        Current Step
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div
                                                style={{
                                                    height: '4px',
                                                    backgroundColor: isCompleted ? '#28a745' : '#e9ecef',
                                                    borderRadius: '2px'
                                                }}
                                            />
                                        </div>
                                        {isCurrent && (
                                            <span
                                                className="badge ms-3"
                                                style={getWorkItemTypeBadgeStyle(step)}
                                            >
                                                In Progress
                                            </span>
                                        )}
                                        {isCompleted && (
                                            <span className="badge bg-success ms-3">
                                                <i className="bi bi-check-circle-fill me-1"></i>
                                                Completed
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Card.Body>
                </Card>

                {/* Content Cards */}
                <Row className="g-4">
                    {/* Application Details */}
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white border-bottom">
                                <h5 className="mb-0">
                                    <i className="bi bi-file-earmark-text me-2"></i>
                                    Application Details
                                </h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="text-muted small mb-1">Customer Name</label>
                                            <div className="fw-medium">{getCustomerName()}</div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="text-muted small mb-1">Application Number</label>
                                            <div className="fw-medium">{application?.applicationNumber || 'N/A'}</div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="text-muted small mb-1">Loan Type</label>
                                            <div className="fw-medium">
                                                {application?.loanType ? String(application.loanType).replace(/_/g, ' ') : 'N/A'}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="text-muted small mb-1">Loan Amount</label>
                                            <div className="fw-medium text-success">
                                                {application?.loanDetails?.loanAmount ? formatCurrency(application.loanDetails.loanAmount) : 'N/A'}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="text-muted small mb-1">Loan Terms</label>
                                            <div className="fw-medium">
                                                {application?.loanDetails?.loanTerms ? `${application.loanDetails.loanTerms} days` : 'N/A'}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-3">
                                            <label className="text-muted small mb-1">Application Status</label>
                                            <div>
                                                {application?.status ? (
                                                    <Badge bg="info">{String(application.status).replace(/_/g, ' ')}</Badge>
                                                ) : (
                                                    'N/A'
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Next Steps Card */}
                        {nextSteps.length > 0 && (
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white border-bottom">
                                    <h5 className="mb-0">
                                        <i className="bi bi-signpost me-2"></i>
                                        Next Steps in Process
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <div className="d-flex flex-column gap-3">
                                        {nextSteps.map((step, index) => (
                                            <div key={step} className="d-flex align-items-center">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                                    style={{
                                                        width: '35px',
                                                        height: '35px',
                                                        backgroundColor: 'rgba(13, 110, 253, 0.1)',
                                                        color: '#0d6efd'
                                                    }}
                                                >
                                                    <small className="fw-bold">{index + 1}</small>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-medium">{formatWorkItemType(step)}</div>
                                                    <small className="text-muted">
                                                        <i className={`bi ${getWorkItemTypeIcon(step)} me-1`}></i>
                                                        Pending
                                                    </small>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>

                    {/* Sidebar */}
                    <Col lg={4}>
                        {/* Assignment Card */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white border-bottom">
                                <h6 className="mb-0">
                                    <i className="bi bi-person-check me-2"></i>
                                    Assignment
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {task.assignee ? (
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                backgroundColor: 'rgba(25, 135, 84, 0.1)',
                                                color: '#198754'
                                            }}
                                        >
                                            <i className="bi bi-person-circle fs-4"></i>
                                        </div>
                                        <div>
                                            <div className="fw-bold">{task.assignee}</div>
                                            <small className="text-success">
                                                <i className="bi bi-check-circle-fill me-1"></i>
                                                Assigned
                                            </small>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                                color: '#ffc107'
                                            }}
                                        >
                                            <i className="bi bi-person-x fs-4"></i>
                                        </div>
                                        <div>
                                            <div className="fw-bold">Unassigned</div>
                                            <small className="text-muted">Available for assignment</small>
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Timeline Card */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white border-bottom">
                                <h6 className="mb-0">
                                    <i className="bi bi-clock-history me-2"></i>
                                    Timeline
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <div className="d-flex flex-column gap-3">
                                    <div className="d-flex align-items-start">
                                        <div
                                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{
                                                width: '35px',
                                                height: '35px',
                                                backgroundColor: 'rgba(13, 110, 253, 0.1)',
                                                color: '#0d6efd',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="bi bi-plus-circle"></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="fw-medium">Created</div>
                                            <div className="text-muted small">
                                                {new Date(task.createdDate).toLocaleDateString()}
                                            </div>
                                            <div className="text-muted small">
                                                {getTimeAgo(task.createdDate)}
                                            </div>
                                        </div>
                                    </div>
                                    {task.dueDate && (
                                        <div className="d-flex align-items-start">
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: '35px',
                                                    height: '35px',
                                                    backgroundColor: dueDateStatus?.status === 'overdue' ? 'rgba(220, 53, 69, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                                                    color: dueDateStatus?.status === 'overdue' ? '#dc3545' : '#ffc107',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="bi bi-flag"></i>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-medium">Due Date</div>
                                                <div className="text-muted small">
                                                    {new Date(task.dueDate).toLocaleDateString()}
                                                </div>
                                                {dueDateStatus && (
                                                    <Badge bg={dueDateStatus.variant} className="mt-1">
                                                        {dueDateStatus.text}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Client Contact Card */}
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-white border-bottom">
                                <h6 className="mb-0">
                                    <i className="bi bi-person-lines-fill me-2"></i>
                                    Client Contact
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {contactInfo.email || contactInfo.phone ? (
                                    <div className="d-flex flex-column gap-3">
                                        {contactInfo.email && (
                                            <div>
                                                <label className="text-muted small mb-1">Email</label>
                                                <div className="d-flex align-items-center">
                                                    <i className="bi bi-envelope me-2 text-primary"></i>
                                                    <a href={`mailto:${contactInfo.email}`} className="text-decoration-none">
                                                        {contactInfo.email}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                        {contactInfo.phone && (
                                            <div>
                                                <label className="text-muted small mb-1">Phone</label>
                                                <div className="d-flex align-items-center">
                                                    <i className="bi bi-telephone me-2 text-primary"></i>
                                                    <a href={`tel:${contactInfo.phone}`} className="text-decoration-none">
                                                        {contactInfo.phone}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                        <div className="d-flex gap-2 pt-2">
                                            {contactInfo.email && (
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    href={`mailto:${contactInfo.email}`}
                                                >
                                                    <i className="bi bi-envelope-open me-1"></i>
                                                    Send Email
                                                </Button>
                                            )}
                                            {contactInfo.phone && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    href={`tel:${contactInfo.phone}`}
                                                >
                                                    <i className="bi bi-telephone-outbound me-1"></i>
                                                    Call Client
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted">
                                        <i className="bi bi-info-circle fs-4 d-block mb-2"></i>
                                        Contact details not available.
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* (Task Details card removed, quick actions moved to header) */}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default WorkItemView;